/* eslint-disable no-console */
import * as Sentry from '@sentry/browser';
import omit from 'lodash/omit';
import { isDev, sentryDSN } from 'config/constants';
import { CheckoutStore } from 'reduxStore/types';
import type { ErrorWithMessage } from 'types';
import { isError } from 'utils/utils';

let enableLogging;
try {
  enableLogging = localStorage.getItem('enable_logging');
} catch (e) {
  enableLogging = false;
}
export const loggingEnabled = isDev || enableLogging;

const sampleRate = isDev ? 1.0 : 0.1;
const tracesSampleRate = 0.001;
const customTracingEnabled = Math.random() < tracesSampleRate;

function info(message: string, ...rest) {
  if (loggingEnabled) {
    console.log(`[honey-checkout-app]: ${message}`, ...rest);
  }
}

function prependErrorMsg(error: unknown, msg: string): ErrorWithMessage {
  const newError = new Error(msg);
  if (isError(error)) {
    newError.message = `${msg}: ${error.message}`;
  }
  return newError;
}

type LoggerErrorExtra = {
  error?: unknown;
  [key: string]: unknown;
};

class Logger {
  reduxStore: CheckoutStore | null = null;

  constructor() {
    Sentry.init({
      beforeSend: (event) => {
        /* eslint-disable no-param-reassign */
        if (this.reduxStore) {
          const state = this.reduxStore.getState();
          /* exclude sensitive data from sentry tracking: */
          const filteredState = omit(state, [
            'app.selectedCardRef.key',
            'user.userEmail',
            'user.eligibility.user',
            'user.eligibility.userEmail',
            'user.phoneNumber',
          ]);
          event.extra = {
            ...event.extra,
            reduxState: filteredState,
          };
        }
        return event;
        /* eslint-enable no-param-reassign */
      },

      dsn: sentryDSN,

      environment: process.env.SENTRY_ENV,

      ignoreErrors: [
        'postMessenger: Time out waiting for target',
      ],

      integrations: [Sentry.browserTracingIntegration()],

      release: process.env.SENTRY_RELEASE_VERSION,

      // Percentage of trace events to be sent out (0 - 1)
      // https://docs.sentry.io/platforms/javascript/configuration/sampling/
      sampleRate,
      tracesSampleRate,
    });

    this.setTags({ buildEnv: process.env.GCP_ENV });
  }

  measure = async <T>(name: string, promise: Promise<T>): Promise<T> => {
    if (customTracingEnabled) {
      return Sentry.startSpan(
        {
          name,
          op: name,
        },
        async (span) => {
          try {
            return await promise;
          } finally {
            span.end();
          }
        },
      );
    } else {
      return promise;
    }
  };

  setReduxStore = (reduxStore) => {
    this.reduxStore = reduxStore;
  };

  setUserId = (userId) => {
    Sentry.getCurrentScope().setUser((scope) => {
      scope.setUser({
        id: userId || null,
      });
    });
  };

  setTags = (tags: { [tagName: string]: string }): void => {
    const scope = Sentry.getCurrentScope();
    if (scope) {
      Object.entries(tags).forEach(([tagName, tagValue]) => {
        scope.setTag(tagName, tagValue);
      });
    }
  };
  
  info = info;

  /* debug property required for honey experiments library */
  debug = info;

  warn = (message: string, ...rest) => {
    if (loggingEnabled) {
      console.warn(`[honey-checkout-app]: ${message}`, ...rest);
    }
  };

  error = (context: string, extra: LoggerErrorExtra = {}): ErrorWithMessage => {
    const error = extra.error ? prependErrorMsg(extra.error, context) : new Error(context);

    if (loggingEnabled) {
      console.error(`[honey-checkout-app]: ${error.message}`, { error, ...extra });
    }

    Sentry.withScope((scope) => {
      Object.entries(extra).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });

    return error;
  };
}

export default new Logger();
